// AID = application identifiers
// these are the codes that mark the different subsections of the barcode
// some of them are preceding fixed-length strings (eg. GTIN, marked by 01, is always 14 digits)

import {gs1Parser} from '@/shared/integrations/scanner/parsers'

const FIXED_LENGTH_AIDS: Record<string, number> = {
  '01': 14,
  '02': 14,
  '11': 6,
  '12': 6,
  '13': 6,
  '14': 6,
  '15': 6,
  '16': 6,
  '17': 6,
  '18': 6,
  '19': 6,
  '20': 2,
}

// this function helps isolating the 710 AI code (german national id)
// which may not be marked with '~' by some barcode scanners

export function normalizeBarcode(raw: string, fncChar = '~'): string {
  // find the 710 candidate
  const targetMatch = raw.match(/7100\d{7}/)
  if (!targetMatch || targetMatch.index === undefined) return raw

  const targetIndex = targetMatch.index

  // walk through the string from the start, consuming known AIs one by one,
  // tracking which character positions are "inside" a known fixed-length value
  const insideFixed = new Set<number>()
  let i = 0

  while (i < targetIndex) {
    // try to match a known fixed-length AI at current position
    const aid = Object.keys(FIXED_LENGTH_AIDS).find(a => raw.startsWith(a, i))

    if (aid) {
      const aidLen = aid.length
      const valueLen = FIXED_LENGTH_AIDS[aid]
      // mark the value positions (not the AI digits themselves) as fixed
      for (let j = i + aidLen; j < i + aidLen + valueLen; j++) {
        insideFixed.add(j)
      }
      i += aidLen + valueLen
      continue
    }

    // skip ~ separators
    if (raw[i] === fncChar) {
      i++
      continue
    }

    // unknown -> skip one character
    i++
  }

  // if the 710 match starts inside a fixed-length value, ignore it
  if (insideFixed.has(targetIndex)) return raw

  // check the character immediately before the match
  const charBefore = targetIndex > 0 ? raw[targetIndex - 1] : null

  // if already preceded by a separator, nothing to do
  if (charBefore === fncChar) return raw

  // otherwise inject the separator
  return raw.slice(0, targetIndex) + fncChar + raw.slice(targetIndex)
}

export function decodeRawBarcode(raw: string) {
  if (!raw) return

  const normalized = normalizeBarcode(raw)
  const result = gs1Parser.decode(normalized)

  return result
}
